import React, { useState, useEffect } from "react";
import {
  Space,
  Card,
  Typography,
  Row,
  Col,
  Tag,
  Descriptions,
  Spin,
  Alert,
} from "antd";
import {
  CheckCircleOutlined,
  DatabaseOutlined,
  PercentageOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import StepHeader from "../../../Evaluation/StepHeader";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";

const { Text } = Typography;

interface DatasetReviewProps {
  datasetData: DatasetData;
  apiKey: string;
}

const DatasetReview: React.FC<DatasetReviewProps> = ({
  datasetData,
  apiKey,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [baseTypeLabel, setBaseTypeLabel] = useState<string>("");
  const [intentGroupName, setIntentGroupName] = useState<string>("");
  const [applicationName, setApplicationName] = useState<string>("");
  const [intentName, setIntentName] = useState<string>("");
  useEffect(() => {
    if (
      datasetData.base_type === "intent_group" &&
      datasetData.intent_group_id
    ) {
      setLoading(true);
      IntentService.getIntentGroup(
        datasetData.intent_group_id,
        false,
        setLoading,
        apiKey
      ).subscribe(
        (response: any) => {
          const name =
            response.intent_name ||
            response.name ||
            `Intent Group ${datasetData.intent_group_id}`;
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(name);
          setLoading(false);
        },
        (error: any) => {
          console.error("Error fetching intent group:", error);
          setBaseTypeLabel("Intent Group");
          setIntentGroupName(`Intent Group ${datasetData.intent_group_id}`);
          setLoading(false);
        }
      );
    } else if (
      datasetData.base_type === "application_intent" &&
      datasetData.application_id &&
      datasetData.intent_id
    ) {
      setLoading(true);
      ApplicationService.getApplication(
        datasetData.application_id,
        setLoading,
        apiKey
      ).subscribe(
        (appResponse: any) => {
          const appName =
            appResponse?.application_name ||
            appResponse?.name ||
            `Application ${datasetData.application_id}`;
          setApplicationName(appName);

          ApplicationService.getApplicationAction(
            datasetData.application_id,
            datasetData.intent_id,
            setLoading,
            apiKey
          ).subscribe(
            (intentResponse: any) => {
              const intName =
                intentResponse?.action_type ||
                `Intent ${datasetData.intent_id}`;
              setBaseTypeLabel("Application/Intent");
              setIntentName(intName);
              setLoading(false);
            },
            (error: any) => {
              console.error("Error fetching intent:", error);
              setBaseTypeLabel("Application/Intent");
              setIntentName(`Intent ${datasetData.intent_id}`);
              setLoading(false);
            }
          );
        },
        (error: any) => {
          console.error("Error fetching application:", error);
          setBaseTypeLabel("Application/Intent");
          setApplicationName(`Application ${datasetData.application_id}`);
          setIntentName(`Intent ${datasetData.intent_id}`);
          setLoading(false);
        }
      );
    }
  }, [datasetData, apiKey]);

  const getSampleTypeLabel = () => {
    switch (datasetData.sample_type) {
      case "all":
        return "All available requests";
      case "random":
        return `Random sample (${datasetData.sample_percentage}%)`;
      case "none":
        return "None (Empty dataset)";
      default:
        return "Not specified";
    }
  };

  const getEstimatedSize = () => {
    if (!datasetData.total_requests) return "0 requests";

    if (datasetData.sample_type === "all") {
      return `${datasetData.total_requests} requests`;
    } else if (
      datasetData.sample_type === "random" &&
      datasetData.sample_percentage
    ) {
      const size = Math.round(
        (datasetData.total_requests * datasetData.sample_percentage) / 100
      );
      return `${size} requests (${datasetData.sample_percentage}% of ${datasetData.total_requests})`;
    } else {
      return "0 requests";
    }
  };

  const getBaseTypeValue = () => {
    if (loading) return <Spin size="small" />;

    if (datasetData.base_type === "intent_group") {
      return (
        <Space>
          <Tag color="#21b892">{baseTypeLabel}</Tag>
          <Text>{intentGroupName}</Text>
        </Space>
      );
    } else {
      return (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space>
            <Tag color="#21b892">{baseTypeLabel}</Tag>
          </Space>
          <Text>Application: {applicationName}</Text>
          <Text>Intent: {intentName}</Text>
        </Space>
      );
    }
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: "100%", display: "flex", alignItems: "center" }}>
      <StepHeader
        title="Review Dataset"
        description="Review your dataset configuration before creating"
        style={{ textAlign: "center" }}
      />

      <Card
        style={{ width: "550px", boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)" }}
        bordered={false}>
        <Descriptions
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px",
              }}>
              <FileTextOutlined
                style={{
                  fontSize: "18px",
                  marginRight: "8px",
                  color: "#21b892",
                }}
              />
              <span>Dataset Summary</span>
            </div>
          }
          bordered
          column={1}
          size="small"
          labelStyle={{ width: "180px", fontWeight: "500" }}
          contentStyle={{ backgroundColor: "#fff" }}>
          <Descriptions.Item label="Name">
            {datasetData.name || "Unnamed Dataset"}
          </Descriptions.Item>

          {datasetData.description && (
            <Descriptions.Item label="Description">
              {datasetData.description}
            </Descriptions.Item>
          )}

          <Descriptions.Item
            label={
              <Space>
                <DatabaseOutlined style={{ color: "#21b892" }} />
                <span>Data Source</span>
              </Space>
            }>
            {getBaseTypeValue()}
          </Descriptions.Item>

          <Descriptions.Item
            label={
              <Space>
                <PercentageOutlined style={{ color: "#21b892" }} />
                <span>Sampling Method</span>
              </Space>
            }>
            {getSampleTypeLabel()}
          </Descriptions.Item>

          <Descriptions.Item label="Estimated Size">
            <Text style={{ color: "#21b892", fontWeight: "500" }}>
              {getEstimatedSize()}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        <Row style={{ marginTop: "16px" }}>
          <Col span={24}>
            <Alert
              message="Ready to Create"
              description="Your dataset is ready to be created. Click the 'Create Dataset' button below to proceed."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>
    </Space>
  );
};

export default DatasetReview;
